// Stable selected-file scope helpers shared by the queue materialiser,
// deterministic pre-processor, and AI readiness gate.

function normalisePath(value: any) {
    return String(value || '')
        .replace(/\\/g, '/')
        .replace(/^\.\//, '')
        .replace(/\/{2,}/g, '/')
        .replace(/^\/+|\/+$/g, '')
        .toLowerCase();
}

function pathFromNamespacedId(value: any) {
    const raw = String(value || '');
    const separator = raw.indexOf('::');
    return separator >= 0 ? normalisePath(raw.slice(separator + 2)) : '';
}

// A selection key with no '::' namespace but a '/' separator is a bare
// relative path (an arrival Included by relPath when no durable file id
// could be resolved). It must count toward relativePaths or the include
// decision matches nothing and the file silently misses the run. Opaque
// Dexie ids (doc_…, photo_…) contain no '/', so they never land here.
function pathFromSelectionKey(value: any) {
    const raw = String(value || '');
    if (raw.includes('::')) return pathFromNamespacedId(raw);
    return raw.includes('/') ? normalisePath(raw) : '';
}

export function createSelectedFileScope(selectedFileIds: any) {
    const cleaned = Array.from(new Set(
        (Array.isArray(selectedFileIds) ? selectedFileIds : [])
            .filter(value => typeof value === 'string' && value.trim())
            .map(value => value.trim()),
    ));
    if (cleaned.length === 0) return null;
    return {
        ids: new Set(cleaned),
        relativePaths: new Set(cleaned.map(pathFromSelectionKey).filter(Boolean)),
        values: cleaned,
    };
}

export function fileMatchesSelectedScope(file: any, scope: any) {
    if (!scope) return true;
    if (!file || typeof file !== 'object') return false;
    const candidateIds = [file.id, file.quoteMakerFileId, file.videoId, file.fileId]
        .filter(value => value !== null && value !== undefined)
        .map(value => String(value).trim())
        .filter(Boolean);
    if (candidateIds.some(id => scope.ids.has(id))) return true;
    const relativePath = normalisePath(
        file.relativePath
        ?? file.quoteMakerSourcePath
        ?? file.sourceRelativePath
        ?? file.path
        ?? file.fileName
        ?? file.name,
    );
    return !!relativePath && scope.relativePaths.has(relativePath);
}

export function filterFilesToSelectedScope(files: any, selectedFileIds: any) {
    const source = Array.isArray(files) ? files : [];
    const scope = createSelectedFileScope(selectedFileIds);
    return scope ? source.filter(file => fileMatchesSelectedScope(file, scope)) : source;
}

export const _internals = { normalisePath, pathFromNamespacedId, pathFromSelectionKey };
